import React, { useState, useEffect, useRef } from 'react';
import { Camera, CheckCircle, XCircle, Loader2, RefreshCw, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SelfieCapture = ({ userId, userName, open, onOpenChange, onSuccess, mode = 'register' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('initializing'); // initializing, ready, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    const startCamera = async () => {
      if (open) {
        setStatus('initializing');
        setCapturedImage(null);
        setErrorMessage('');
        
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: 'user', 
              width: { ideal: 640 }, 
              height: { ideal: 480 } 
            }
          });
          
          setStream(mediaStream);
          
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.onloadedmetadata = () => {
              setStatus('ready');
            };
          }
        } catch (error) {
          console.error('Error accessing camera:', error);
          setStatus('error');
          if (error.name === 'NotAllowedError') {
            setErrorMessage('Camera access denied. Please allow camera permissions in your browser settings.');
          } else if (error.name === 'NotFoundError') {
            setErrorMessage('No camera found. Please ensure your device has a camera.');
          } else {
            setErrorMessage('Failed to access camera. Please try again.');
          }
        }
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [open]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Camera not ready');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    // Mirror the image for selfie view
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    setStatus('success');
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setStatus('ready');
  };

  const handleSave = async () => {
    if (!capturedImage) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/employees/${userId}/register-selfie`,
        { selfie_image: capturedImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Photo saved successfully!');
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error saving photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to save photo');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
    setCapturedImage(null);
    setStatus('initializing');
    setErrorMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-wt-surface border-wt-border">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <User className="h-5 w-5 text-wt-gold" />
            {mode === 'register' ? 'Register Photo' : 'Take Selfie'}{userName ? `: ${userName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Error State */}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-12 w-12 text-red-400 mb-4" />
              <p className="text-red-400 text-center text-sm px-4 mb-4">{errorMessage}</p>
              <Button 
                onClick={() => { setStatus('initializing'); onOpenChange(true); }} 
                variant="outline"
                className="border-wt-border"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}

          {/* Initializing State */}
          {status === 'initializing' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-10 w-10 animate-spin text-wt-gold mb-4" />
              <p className="text-wt-text-muted">Starting camera...</p>
            </div>
          )}

          {/* Camera View */}
          {(status === 'ready' || status === 'initializing') && !capturedImage && (
            <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Overlay guide */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-40 h-48 border-2 border-dashed border-wt-gold/50 rounded-full" />
              </div>
            </div>
          )}

          {/* Captured Image Preview */}
          {status === 'success' && capturedImage && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <img 
                  src={capturedImage} 
                  alt="Captured selfie" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-green-400 text-sm">Photo captured!</span>
              </div>
            </div>
          )}

          {/* Instructions */}
          {status === 'ready' && !capturedImage && (
            <div className="flex items-start gap-2 p-3 bg-wt-bg border border-wt-border rounded-lg">
              <AlertCircle className="h-5 w-5 text-wt-gold mt-0.5 flex-shrink-0" />
              <div className="text-sm text-wt-text-muted">
                <p className="font-medium text-wt-text-primary mb-1">Tips:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Look directly at the camera</li>
                  <li>Ensure good lighting</li>
                  <li>Keep a neutral expression</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            {status === 'ready' && !capturedImage && (
              <Button
                onClick={handleCapture}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="capture-selfie-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Capture Photo
              </Button>
            )}
            
            {status === 'success' && capturedImage && (
              <>
                <Button 
                  onClick={handleRetake} 
                  variant="outline" 
                  className="border-wt-border"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="save-selfie-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Save Photo
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelfieCapture;
